import { toast } from 'react-hot-toast';

// ✅ Use environment variables
export const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;

// Cognito configuration (for token refresh)
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN;
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_COGNITO_CLIENT_SECRET;

// ✅ Validation
if (!API_ENDPOINT || !COGNITO_DOMAIN || !COGNITO_CLIENT_ID || !CLIENT_SECRET) {
  console.error('❌ Missing required environment variables in api.js');
}

/**
 * Get auth token from localStorage
 */
function getAuthToken() {
  return localStorage.getItem('idToken');
}

/**
 * Refresh expired tokens using refresh_token
 */
async function refreshTokens() {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const params = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: COGNITO_CLIENT_ID,
      refresh_token: refreshToken
    });

    const basicAuth = btoa(`${COGNITO_CLIENT_ID}:${CLIENT_SECRET}`);

    const response = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'Authorization': `Basic ${basicAuth}`
      },
      body: params.toString()
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    
    // Store new tokens
    localStorage.setItem('idToken', data.id_token);
    localStorage.setItem('accessToken', data.access_token);
    
    console.log('✅ Tokens refreshed successfully');
    
    // ✅ Return accessToken (not idToken)
    return data.access_token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    
    // Clear invalid tokens and redirect to login
    localStorage.removeItem('idToken');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('organizationId');
    localStorage.removeItem('organizationRole');
    localStorage.removeItem('organizationEmail');
    
    toast.error('Session expired. Please log in again.');
    
    // Redirect to login
    window.location.href = '/login';
    
    throw error;
  }
}

/**
 * Check if token is expired
 */
function isTokenExpired(token) {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    
    // Consider token expired if it expires in the next 5 minutes
    return Date.now() >= (expirationTime - 5 * 60 * 1000);
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
}

/**
 * Get a valid token, refreshing if necessary
 */
async function getValidToken() {
  // ✅ USE accessToken instead of idToken for API calls
  let token = localStorage.getItem('idToken');
  
  if (isTokenExpired(token)) {
    console.log('Token expired, refreshing...');
    token = await refreshTokens();
  }
  
  return token;
}

/**
 * Helper function to make authenticated API calls with automatic error handling
 * Automatically handles token refresh on 401 errors
 * Returns parsed JSON data on success, throws error on failure
 */
export async function fetchWithAuth(endpoint, organizationId, retryCount = 0) {
  if (!organizationId) {
    const error = new Error('Organization ID is required');
    toast.error('Authentication error: Missing organization ID');
    throw error;
  }

  try {
    const token = await getValidToken();
    
    // Build URL with query parameters
    const separator = endpoint.includes('?') ? '&' : '?';
    const url = `${API_ENDPOINT}${endpoint}${separator}organization_id=${organizationId}`;
    
    console.log('🌐 Fetching:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    // Handle 401 - token expired, try refreshing once
    if (response.status === 401 && retryCount === 0) {
      console.log('🔄 Received 401, attempting token refresh...');
      
      try {
        await refreshTokens();
        // Retry the request with new token (increment retryCount to prevent infinite loop)
        return await fetchWithAuth(endpoint, organizationId, retryCount + 1);
      } catch (refreshError) {
        // Token refresh failed, user needs to log in again
        throw new Error('Authentication failed. Please log in again.');
      }
    }

    // Handle other errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `Request failed with status ${response.status}`;
      
      // Don't show toast for 401 (already handled above)
      if (response.status !== 401) {
        toast.error(errorMessage);
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
    
  } catch (error) {
    console.error(`Error fetching ${endpoint}:`, error);
    
    // Only show toast if it's not a refresh-related error (already shown)
    if (!error.message.includes('Session expired') && 
        !error.message.includes('Authentication failed') &&
        !error.message.includes('Request failed')) {
      toast.error(`Network error: ${error.message}`);
    }
    
    throw error;
  }
}

/**
 * Helper for POST/PUT/DELETE requests with authentication
 * Automatically handles token refresh on 401 errors
 * Returns parsed JSON data on success
 */
export async function authenticatedFetch(url, options = {}, retryCount = 0) {
  try {
    const token = await getValidToken();
    
    console.log('🌐 Authenticated Fetch:', url, options.method || 'POST');
    
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      }
    });

    // Handle 401 - token expired, try refreshing once
    if (response.status === 401 && retryCount === 0) {
      console.log('🔄 Received 401, attempting token refresh...');
      
      try {
        await refreshTokens();
        // Retry the request with new token
        return await authenticatedFetch(url, options, retryCount + 1);
      } catch (refreshError) {
        throw new Error('Authentication failed. Please log in again.');
      }
    }

    // Handle other errors
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = errorData.error || `Request failed with status ${response.status}`;
      
      if (response.status !== 401) {
        toast.error(errorMessage);
      }
      
      throw new Error(errorMessage);
    }

    return await response.json();
    
  } catch (error) {
    console.error('Fetch error:', error);
    
    if (!error.message.includes('Session expired') && 
        !error.message.includes('Authentication failed')) {
      toast.error(`Network error: ${error.message}`);
    }
    
    throw error;
  }
}

/**
 * Check if user is authenticated (has valid tokens)
 */
export function isAuthenticated() {
  const token = getAuthToken();
  return token && !isTokenExpired(token);
}

/**
 * Manually trigger logout (clears all tokens)
 */
export function clearAuthTokens() {
  localStorage.removeItem('idToken');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('organizationId');
  localStorage.removeItem('organizationRole');
  localStorage.removeItem('organizationEmail');
}